import React, { useEffect, useMemo, useState } from "react";
import Alert from "react-bootstrap/Alert";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import OverlayTrigger from "react-bootstrap/OverlayTrigger";
import Spinner from "react-bootstrap/Spinner";
import Tooltip from "react-bootstrap/Tooltip";

import {
  sendWhatsAppTemplate,
  sendWhatsAppText,
  setWhatsAppConsent,
} from "../../whatsappApi";

/**
 * Composer: window-aware reply box.
 *
 * Three states, because WhatsApp genuinely has three:
 *   open window   → free text
 *   closed window → the textarea is REPLACED (not merely disabled) by a
 *                   template send, because typing a message that cannot be
 *                   sent is the thing to prevent
 *   no consent / opted out → blocked entirely, with an explanation
 *
 * The no-consent state is shown BEFORE the user types, not as a refusal after
 * they press send. A patient who messaged the practice is granted consent
 * automatically on ingest, so reaching this state means nobody has recorded it,
 * and the fix is offered inline rather than sending the user to the patient
 * record and back.
 *
 * Every send, text or template, puts a bubble in the thread the moment it is
 * pressed, and that bubble either resolves to the sent message or turns into a
 * failed one carrying the reason. A button that appears to do nothing is the
 * worst outcome available here: the user presses it again, and again.
 *
 * The server enforces all of this too (a stale tab would otherwise post into a
 * window that closed two minutes ago); this is the affordance, not the rule.
 */

/** Short-lived client id for the optimistic bubble, replaced by the real row. */
let pendingSeq = 0;
const nextPendingId = () => `pending-${(pendingSeq += 1)}`;

/** Resolve {{n}} locally so the pending bubble shows the real wording. */
function renderTemplate(body, params) {
  if (!body) return "";
  return String(body).replace(/\{\{\s*(\d+)\s*\}\}/g, (_, n) => {
    const value = params[Number(n) - 1];
    return value === undefined || value === null ? "" : String(value);
  });
}

/** Whoever this thread is with: patient, account holder, or just a number. */
function subjectName(conversation) {
  return (
    conversation.patientName ||
    conversation.accountName ||
    conversation.profileName ||
    ""
  ).trim();
}

export default function Composer({
  practiceNumber,
  practiceName,
  conversation,
  templates,
  onPending,
  onSent,
  onFailed,
  onConsentChanged,
  showToast,
}) {
  const [body, setBody] = useState("");
  const [sending, setSending] = useState(false);
  const [recordingConsent, setRecordingConsent] = useState(false);
  const [templateName, setTemplateName] = useState("");

  /**
   * Only the templates a person is allowed to send by hand.
   *
   * `manualSend` is decided server-side (whatsappTemplateService) and enforced
   * on the send route, so this filter cannot drift from the rule. It keeps out
   * Meta's `hello_world` sample (which ships with every WABA and is not a
   * message any practice means to send a patient) and the appointment
   * templates, which want a date and a time the inbox does not have and so
   * could only ever fail on the parameter count.
   */
  const approved = useMemo(
    () => templates.filter((t) => t.status === "APPROVED" && t.manualSend),
    [templates],
  );

  // Never leave a name selected that is not in the list: the previous default
  // was hard-coded, so a practice whose re-engagement template had not been
  // approved got a send button wired to nothing.
  useEffect(() => {
    setTemplateName((current) =>
      approved.some((t) => t.name === current) ? current : approved[0]?.name || "",
    );
  }, [approved]);

  if (!conversation) return null;

  const blocked = conversation.optedOut;

  // A thread belongs to exactly one subject: the patient or the account holder
  // it was opened for. A number nobody has claimed is neither, and a template
  // to an unidentified number is refused server-side. We do NOT offer to link
  // it to a patient here. An account holder and a patient can share a number
  // and still be different people, and an account can carry several patients,
  // so guessing the match from a number is exactly the wrong move.
  const unidentified = !conversation.patientId && !conversation.accountId;

  // `consent` is undefined when the caller did not load it and null when the
  // thread has no patient, so only a patient thread can report consent missing.
  const consent = conversation.consent;
  const withdrawn = !!consent && consent.decided && !consent.granted;
  const consentMissing = !!consent && !consent.decided && !consent.granted;

  const recordConsent = async () => {
    setRecordingConsent(true);
    try {
      await setWhatsAppConsent(conversation.patientId, true, "practice");
      showToast?.("success", "WhatsApp consent recorded for this patient.");
      onConsentChanged?.();
    } catch (err) {
      showToast?.(
        "danger",
        err?.response?.data?.error || "The consent could not be recorded.",
      );
    } finally {
      setRecordingConsent(false);
    }
  };

  /**
   * A refused send still has to show in the thread.
   *
   * Two shapes of failure and both land as a failed bubble: Meta rejected it
   * (the server wrote the row and returns it as `message`), or the server
   * refused before calling Meta at all (no row, so the pending bubble is marked
   * failed with the reason). Previously the second case produced nothing but a
   * toast, so a template that could not be sent looked like a dead button.
   */
  const fail = (pendingId, err, fallback) => {
    const reason = err?.response?.data?.error || fallback;
    onFailed?.(pendingId, reason, err?.response?.data?.message || null);
    showToast?.("danger", reason);
  };

  const sendText = async () => {
    if (!body.trim()) return;
    const text = body;
    const pendingId = nextPendingId();

    setSending(true);
    setBody("");
    onPending?.({
      _id: pendingId,
      direction: "outbound",
      type: "text",
      status: "sending",
      body: text,
      createdAt: new Date().toISOString(),
    });

    try {
      const { message } = await sendWhatsAppText(
        practiceNumber,
        conversation._id,
        text,
      );
      onSent?.(pendingId, message);
    } catch (err) {
      fail(pendingId, err, "The message could not be sent.");
    } finally {
      setSending(false);
    }
  };

  const sendTemplate = async () => {
    const template = approved.find((t) => t.name === templateName);
    if (!template) return;

    // The re-engagement template takes the person's first name then the
    // practice name. The practice name comes down as a prop rather than off
    // the conversation, which never carried one, so "your practice" was going
    // out in real messages.
    const params = [
      subjectName(conversation).split(/\s+/)[0] || "there",
      practiceName || "your practice",
    ].slice(0, template.paramCount);

    if (params.length !== template.paramCount) {
      showToast?.(
        "danger",
        "This template needs values the inbox cannot supply. It is sent automatically instead.",
      );
      return;
    }

    const pendingId = nextPendingId();
    setSending(true);
    onPending?.({
      _id: pendingId,
      direction: "outbound",
      type: "template",
      status: "sending",
      templateName: template.name,
      body: renderTemplate(template.body, params),
      createdAt: new Date().toISOString(),
    });

    try {
      const { message } = await sendWhatsAppTemplate(practiceNumber, {
        conversationId: conversation._id,
        templateName: template.name,
        languageCode: template.language,
        params,
      });
      onSent?.(pendingId, message);
    } catch (err) {
      fail(pendingId, err, "The template could not be sent.");
    } finally {
      setSending(false);
    }
  };

  // A staff withdrawal on the patient record reads the same to the user as an
  // inbound STOP, so it shares this surface rather than inventing a second one.
  if (blocked || withdrawn) {
    return (
      <div className="wa-composer">
        <Alert variant="warning" className="mb-0">
          This patient has opted out of WhatsApp. You cannot send messages until
          they opt in again on their patient record.
        </Alert>
      </div>
    );
  }

  // Nothing recorded either way: the send would be refused by the server, so the
  // consent is asked for here rather than letting the user discover that.
  if (consentMissing) {
    return (
      <div className="wa-composer">
        <Alert variant="warning" className="mb-0 d-flex align-items-center gap-2">
          <span className="flex-fill">
            No WhatsApp consent is recorded for this patient, so messages cannot
            be sent yet.
          </span>
          <OverlayTrigger
            placement="top"
            overlay={
              <Tooltip>
                Record that the patient has agreed to WhatsApp messages about
                their appointments and account.
              </Tooltip>
            }
          >
            <Button
              variant="outline-primary"
              size="sm"
              onClick={recordConsent}
              disabled={recordingConsent}
            >
              {recordingConsent ? (
                <Spinner size="sm" animation="border" />
              ) : (
                "Record consent"
              )}
            </Button>
          </OverlayTrigger>
        </Alert>
      </div>
    );
  }

  return (
    <div className="wa-composer">
      {conversation.windowOpen ? (
        <div className="d-flex align-items-end gap-2">
          <Form.Control
            as="textarea"
            rows={2}
            value={body}
            placeholder="Type a reply…"
            onChange={(e) => setBody(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                sendText();
              }
            }}
            disabled={sending}
          />
          <OverlayTrigger placement="top" overlay={<Tooltip>Send</Tooltip>}>
            <Button
              variant="primary"
              onClick={sendText}
              disabled={sending || !body.trim()}
            >
              {sending ? (
                <Spinner size="sm" animation="border" />
              ) : (
                <i className="fa-solid fa-paper-plane" />
              )}
            </Button>
          </OverlayTrigger>
        </div>
      ) : (
        <>
          <Alert variant="info" className="py-2 px-3 mb-2">
            {unidentified
              ? "This number is not a patient or an account holder on this practice, so a template cannot be sent to it. You can only reply freely within 24 hours of their last message."
              : "You can only send a free message within 24 hours of the patient's last message. Send an approved template to reopen the conversation."}
          </Alert>

          {!unidentified && !approved.length && (
            <Alert variant="warning" className="py-2 px-3 mb-2">
              No template is approved for sending by hand yet. Provision the
              standard set in Practice Settings.
            </Alert>
          )}

          {/* One button per sendable template, matched widths per §1.4. With a
              single template the row would be a picker with nothing to pick. */}
          {approved.length > 1 && (
            <div className="d-flex flex-wrap gap-2 mb-2">
              {approved.map((t) => (
                <Button
                  key={`${t.name}-${t.language}`}
                  size="sm"
                  style={{ minWidth: 120 }}
                  variant={
                    templateName === t.name ? "secondary" : "outline-secondary"
                  }
                  onClick={() => setTemplateName(t.name)}
                >
                  {t.name.replace("practosync_", "").replace(/_/g, " ")}
                </Button>
              ))}
            </div>
          )}

          <Button
            variant="primary"
            onClick={sendTemplate}
            disabled={sending || unidentified || !approved.length}
          >
            {sending ? (
              <Spinner size="sm" animation="border" />
            ) : (
              <>
                <i className="fa-solid fa-paper-plane me-2" />
                Send greeting
              </>
            )}
          </Button>
        </>
      )}
    </div>
  );
}
